import { findFormDataKey } from './formSchemaFields';
import { parseBackendDate } from './datetime';
// Offline querying for the registrations table. Best-effort mirror of the
// backend filtered-registrations endpoint (search + filters + sort + paging)
// applied over the locally cached records. The backend remains authoritative
// when online; this only runs when the network is unavailable.

import { getAllEventRegistrations } from './offlineDb';

// Internal/index fields we never want to treat as user data when searching.
const INTERNAL_FIELDS = new Set(['key', 'rfidKey', 'eventId']);

function stringifyValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return v.toString();
}

// A record matches the search term if any form field value (or userId/rfId)
// contains the term, case-insensitively.
// Mirrors the backend search: name fields match anywhere ignoring spaces/punctuation ("vk" finds
// "Dr V K Singh"); everything else matches from the start, all case-insensitive.
const compact = (value) => String(value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

function matchesSearch(reg, term) {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return true;
  const startsWith = (value) => stringifyValue(value).trim().toLowerCase().startsWith(needle);
  const compactNeedle = compact(needle);
  const nameMatches = (value) => (compactNeedle
    ? compact(stringifyValue(value)).includes(compactNeedle)
    : stringifyValue(value).toLowerCase().includes(needle));
  if (startsWith(reg.userId) || startsWith(reg.rfId)) return true;
  const formData = reg.formData || {};
  return Object.keys(formData).some((key) => (key.toLowerCase().includes('name')
    ? nameMatches(formData[key])
    : startsWith(formData[key])));
}

function fieldValue(reg, field) {
  if (!field) return undefined;
  if (reg.formData && reg.formData[field] !== undefined) return reg.formData[field];
  if (!INTERNAL_FIELDS.has(field) && reg[field] !== undefined) return reg[field];
  return undefined;
}

// filters: { fieldName: value | [values] }. A record matches if, for every
// active filter, its value equals (or is one of) the requested value(s).
function matchesFilters(reg, filters) {
  if (!filters) return true;
  for (const [field, expected] of Object.entries(filters)) {
    if (expected === null || expected === undefined || expected === '') continue;
    if (field.startsWith('checkins.')) {
      const checkedIn = !!reg.checkins?.[field.slice('checkins.'.length)];
      if (checkedIn !== (String(expected).toLowerCase() !== 'false')) return false;
      continue;
    }
    const actual = stringifyValue(field.startsWith('formData.')
      ? reg.formData?.[findFormDataKey(reg.formData || {}, field.slice('formData.'.length))]
      : fieldValue(reg, field)).toLowerCase();
    if (Array.isArray(expected)) {
      if (expected.length === 0) continue;
      const wanted = expected.map((e) => stringifyValue(e).toLowerCase());
      if (!wanted.includes(actual)) return false;
    } else if (actual !== stringifyValue(expected).toLowerCase()) {
      return false;
    }
  }
  return true;
}

// Stored timestamps are UTC without a zone designator.
function recordTime(value) {
  const d = parseBackendDate(value);
  return d ? d.getTime() : null;
}

// Date-range inputs ("YYYY-MM-DD HH:mm") are picked in IST.
function istInputTime(value) {
  if (!value) return null;
  const t = Date.parse(`${String(value).trim().replace(' ', 'T')}:00+05:30`);
  return Number.isNaN(t) ? null : t;
}

function withinDateRange(reg, startDate, endDate) {
  if (!startDate && !endDate) return true;
  const raw = reg.registeredAt || reg.createdAt;
  const t = recordTime(raw);
  if (t === null) return true; // don't drop records we can't date
  const start = istInputTime(startDate);
  const end = istInputTime(endDate);
  if (start !== null && t < start) return false;
  if (end !== null && t > end) return false;
  return true;
}

function compareBy(field, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  return (a, b) => {
    let av = fieldValue(a, field);
    let bv = fieldValue(b, field);
    // createdAt/registeredAt should sort chronologically.
    const at = typeof av === 'string' ? recordTime(av) : null;
    const bt = typeof bv === 'string' ? recordTime(bv) : null;
    if (at !== null && bt !== null) return (at - bt) * dir;
    av = stringifyValue(av).toLowerCase();
    bv = stringifyValue(bv).toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  };
}

/**
 * Pure filter + sort + paginate over an array of cached registrations.
 * Returns a Spring-Page-shaped object matching the online response.
 */
export function filterSortPage(records = [], request = {}) {
  const {
    filters = {},
    searchTerm = '',
    page = 0,
    size = 20,
    sortBy = 'createdAt',
    sortDirection = 'desc',
    startDate = null,
    endDate = null,
  } = request;

  const filtered = records.filter(
    (r) =>
      matchesSearch(r, searchTerm) &&
      matchesFilters(r, filters) &&
      withinDateRange(r, startDate, endDate)
  );

  filtered.sort(compareBy(sortBy, sortDirection));

  const totalElements = filtered.length;
  const pageSize = size > 0 ? size : 20;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const current = Math.min(Math.max(page, 0), totalPages - 1);
  const startIdx = current * pageSize;
  const content = filtered.slice(startIdx, startIdx + pageSize);

  return {
    content,
    totalElements,
    totalPages,
    size: pageSize,
    number: current,
    first: current === 0,
    last: current >= totalPages - 1,
    numberOfElements: content.length,
    offline: true,
  };
}

/** Load cached registrations for an event and apply the query. */
export async function queryRegistrationsOffline(eventId, request = {}) {
  const records = await getAllEventRegistrations(eventId);
  return filterSortPage(records, request);
}
